import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';
import { SocialLinks } from '../SocialLinks';

const HOURS = [
  { day: 'Sunday', time: 'Closed' },
  { day: 'Monday – Thursday', time: '9am – 6pm' },
  { day: 'Friday', time: '8am – 2pm' },
  { day: 'Saturday', time: 'Closed' },
];

const FAQ_LINKS = [
  { to: '/privacy-policy', label: 'Privacy Policy', testid: 'privacy-policy' },
  { to: '/terms/general', label: 'Terms & Conditions', testid: 'general' },
  { to: '/terms/cleaning-maintenance', label: 'Cleaning & Maintenance Terms & Conditions', testid: 'cleaning-maintenance' },
  { to: '/terms/platinum-prestige', label: 'Platinum Prestige Terms & Conditions', testid: 'platinum-prestige' },
  { to: '/terms/promotions', label: 'Promotion Terms & Conditions', testid: 'promotions' },
];

const PAYMENT_METHODS = [
  { src: '/payments/amex.svg', alt: 'American Express' },
  { src: '/payments/visa.svg', alt: 'Visa' },
  { src: '/payments/mastercard.svg', alt: 'Mastercard' },
  { src: '/payments/discover.svg', alt: 'Discover' },
  { src: '/payments/usdt.svg', alt: 'USDT (Tether)' },
  { src: '/payments/btc.svg', alt: 'Bitcoin' },
  { src: '/payments/sol.svg', alt: 'Solana' },
  { src: '/payments/eth.svg', alt: 'Ethereum' },
  { src: '/payments/xrp.svg', alt: 'XRP' },
];

export const Footer = () => {
  const { c } = useSiteContent();
  const email = c('global.email', 'info@dreamroomtheaters.com');
  const phone = c('global.phone', '1-800-967-7043');
  return (
    <footer className="bg-[#0a0a0b] border-t border-white/5" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Logo */}
          <div className="lg:col-span-1 flex lg:justify-start">
            <Link to="/" className="inline-block">
              <img src="/brand/logo-icon.png" alt="DreamRoom Theaters" className="h-24 w-auto" data-testid="footer-logo" />
            </Link>
          </div>

          {/* About */}
          <div className="lg:col-span-1">
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-3">About</h4>
            <p className="text-white/30 text-sm font-manrope leading-relaxed">
              Discover premium surround sound systems, high-end projectors and screens, custom mini bars, movie memorabilia, and concession stands—everything you need for the ultimate cinematic experience.
            </p>
          </div>

          {/* Hours of Operation */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-4">Hours of Operation</h4>
            <div className="space-y-2">
              {HOURS.map(h => (
                <div key={h.day} className="text-sm font-manrope">
                  <span className="text-white/40">{h.day}</span>
                  <span className={`block text-xs ${h.time === 'Closed' ? 'text-white/20' : 'text-[#D4AF37]/70'}`}>{h.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reach Us */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-4">Reach Us</h4>
            <div className="space-y-3">
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-white/40 hover:text-[#D4AF37] text-sm font-manrope transition-colors duration-300" data-testid="footer-email">
                <Mail size={14} className="text-[#D4AF37] flex-shrink-0" />
                <span>{email}</span>
              </a>
              <a href={`tel:${phone}`} className="flex items-center gap-3 text-white/40 hover:text-[#D4AF37] text-sm font-manrope transition-colors duration-300" data-testid="footer-phone">
                <Phone size={14} className="text-[#D4AF37] flex-shrink-0" />
                <span>{phone}</span>
              </a>
            </div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mt-6 mb-3">Follow Us</h4>
            <SocialLinks size={16} />
          </div>

          {/* Business */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-4">Business</h4>
            <div className="space-y-2">
              {[
                { to: '/contact', label: 'Developers' },
                { to: '/packages', label: 'Our Packages' },
                { to: '/book', label: 'Book a Consultation' },
                { to: '/portal', label: 'Client Portal Login' },
                { to: '/admin', label: 'Admin Login' },
              ].map(link => (
                <Link key={link.label} to={link.to} className="block text-white/40 hover:text-[#D4AF37] text-sm font-manrope transition-colors duration-300">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* FAQs */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-4">FAQs</h4>
            <div className="space-y-2">
              {FAQ_LINKS.map(link => (
                <Link key={link.to} to={link.to} className="block text-white/40 hover:text-[#D4AF37] text-sm font-manrope transition-colors duration-300" data-testid={`footer-legal-${link.testid}`}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3 flex-wrap justify-center" data-testid="payment-methods">
            {PAYMENT_METHODS.map(pm => (
              <img
                key={pm.alt}
                src={pm.src}
                alt={pm.alt}
                title={pm.alt}
                className="h-8 w-auto rounded-[3px] opacity-90 hover:opacity-100 transition-opacity duration-300"
              />
            ))}
          </div>
          <p className="text-white/20 text-xs font-manrope">
            &copy; {new Date().getFullYear()}. Dream Room Theaters. All rights reserved
          </p>
        </div>
      </div>
    </footer>
  );
};
